import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import EquipmentLabelDialog from './EquipmentLabelDialog';
import ReportBreakdownDialog from './ReportBreakdownDialog';
import { MaintenanceService } from '../../services/maintenance.service';
import { AuthService } from '../../services/auth.service';
import { DepartmentService } from '../../services/department.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import {
    EQUIPMENT_STATUSES, EquipmentDTO, EquipmentMoveDTO, EquipmentStatus, METER_KINDS, MeterDTO, MeterKind,
    MeterRequest, PmPlanDTO, WorkOrderDTO, mntLabel,
} from '../../interfaces/maintenance';
import { Department } from '../../interfaces/department.interface';
import { STATUS_COLOR } from './EquipmentList';
import { useEquipmentGroups } from './useEquipmentGroups';
import { PM_STATE_COLOR, PM_STATE_LABEL, pmNextDueText, pmTriggerText } from './pmCommon';

const EMPTY_METER: MeterRequest = { code: '', name: '', kind: 'GAUGE', unit: '', isPmDriver: false, active: true };

const Fact: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ flex: '1 1 200px', maxWidth: 320, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{value === undefined || value === null || value === '' ? '-' : value}</Typography>
    </Box>
);

/** Equipment detail: facts, meters, location history, move / status / delete actions. */
const EquipmentDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.MAINTENANCE_EQUIPMENT_MANAGE);
    const canViewPm = hasPermission(PERMISSIONS.MAINTENANCE_PM_VIEW);
    const canViewWo = hasPermission(PERMISSIONS.MAINTENANCE_WO_VIEW_ALL);
    const [params] = useSearchParams();
    const fromQr = params.get('from') === 'qr';
    const [workOrders, setWorkOrders] = useState<WorkOrderDTO[]>([]);
    const [labelOpen, setLabelOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [pmPlans, setPmPlans] = useState<PmPlanDTO[]>([]);
    const [eq, setEq] = useState<EquipmentDTO | null>(null);
    const { label: groupName } = useEquipmentGroups(AuthService.getSelectedInstitution() ?? '');
    const [moves, setMoves] = useState<EquipmentMoveDTO[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [loading, setLoading] = useState(true);

    const [moveOpen, setMoveOpen] = useState(false);
    const [toDept, setToDept] = useState('');
    const [reason, setReason] = useState('');
    const [statusOpen, setStatusOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<EquipmentStatus>('RUNNING');
    const [meterOpen, setMeterOpen] = useState<{ id?: string; req: MeterRequest } | null>(null);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const [e, m] = await Promise.all([MaintenanceService.getEquipment(id), MaintenanceService.listMoves(id)]);
            setEq(e);
            setMoves(m);
        } catch (err) {
            toast.showApiError(err, 'Failed to load equipment');
        } finally {
            setLoading(false);
        }
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!id || !canViewPm) return;
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        MaintenanceService.listPmForEquipment(institutionId, id).then(setPmPlans).catch(() => setPmPlans([]));
    }, [id, canViewPm]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!id || !canViewWo) return;
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        MaintenanceService.listWorkOrders(institutionId, { equipmentId: id }, 0, 10)
            .then(r => setWorkOrders(r.content)).catch(() => setWorkOrders([]));
    }, [id, canViewWo]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        DepartmentService.getLocationOptions().then(r => setDepartments(r.content ?? [])).catch(() => setDepartments([]));
    }, []);

    const run = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
        try {
            await fn();
            toast.showSuccess(ok);
            await load();
            return true;
        } catch (e) {
            toast.showApiError(e, fail);
            return false;
        }
    };

    const doMove = async () => {
        if (!toDept) { toast.showError('Choose the new location'); return; }
        if (await run(() => MaintenanceService.moveEquipment(id!, toDept, reason), 'Equipment moved', 'Failed to move equipment')) {
            setMoveOpen(false); setToDept(''); setReason('');
        }
    };

    const doStatus = async () => {
        if (await run(() => MaintenanceService.setStatus(id!, newStatus, reason), 'Status updated', 'Failed to change status')) {
            setStatusOpen(false); setReason('');
        }
    };

    const doDelete = async () => {
        if (!window.confirm(`Delete equipment "${eq?.code}"?`)) return;
        try {
            await MaintenanceService.deleteEquipment(id!);
            toast.showSuccess('Equipment deleted');
            navigate('/maintenance/equipment');
        } catch (e) {
            toast.showApiError(e, 'Failed to delete equipment');
        }
    };

    const saveMeter = async () => {
        if (!meterOpen) return;
        const req = { ...meterOpen.req, unit: meterOpen.req.unit?.trim() || null };
        if (!req.code.trim() || !req.name.trim()) { toast.showError('Meter code and name are required'); return; }
        const call = meterOpen.id ? () => MaintenanceService.updateMeter(meterOpen.id!, req) : () => MaintenanceService.addMeter(id!, req);
        if (await run(call, 'Meter saved', 'Failed to save meter')) setMeterOpen(null);
    };

    const removeMeter = (m: MeterDTO) => {
        if (!window.confirm(`Delete meter "${m.name}"?`)) return;
        run(() => MaintenanceService.deleteMeter(m.id), 'Meter deleted', 'Failed to delete meter');
    };

    if (loading && !eq) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    if (!eq) return <Box sx={{ p: 3 }}><Typography>Equipment not found</Typography></Box>;

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }} data-help-page="maint.equipmentDetail.page">
            {fromQr && eq.status !== 'RETIRED' && (
                <Button data-help="maint.equipmentDetail.reportBreakdown" variant="contained" color="error" size="large" fullWidth
                    startIcon={<ReportProblemIcon />} sx={{ mb: 2, py: 1.5 }} onClick={() => setReportOpen(true)}>
                    Report a breakdown
                </Button>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="h5">{eq.code} - {eq.name}</Typography>
                    <Chip size="small" label={mntLabel(eq.status)} color={STATUS_COLOR[eq.status]} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button onClick={() => navigate('/maintenance/equipment')}>Back</Button>
                    {canManage && eq.status !== 'RETIRED' && (
                        <>
                            <Button data-help="maint.equipmentDetail.status" onClick={() => { setNewStatus(eq.status); setReason(''); setStatusOpen(true); }}>Change status</Button>
                            <Button data-help="maint.equipmentDetail.move" onClick={() => { setToDept(''); setReason(''); setMoveOpen(true); }}>Move</Button>
                        </>
                    )}
                    {canManage && <Button data-help="maint.equipmentDetail.printLabel" startIcon={<QrCode2Icon />} onClick={() => setLabelOpen(true)}>Print label</Button>}
                    {canManage && <Button data-help="maint.equipmentDetail.edit" startIcon={<EditIcon />} onClick={() => navigate(`/maintenance/equipment/${eq.id}/edit`)}>Edit</Button>}
                    {canManage && <Button data-help="maint.equipmentDetail.delete" color="error" startIcon={<DeleteIcon />} onClick={doDelete}>Delete</Button>}
                </Box>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.equipmentDetail.facts">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Fact label="Type" value={eq.equipmentTypeName} />
                    <Fact label="Group" value={groupName(eq.category)} />
                    <Fact label="Location" value={eq.locationDepartmentName} />
                    <Fact label="Criticality" value={eq.criticality} />
                    <Fact label="Manufacturer" value={eq.manufacturer} />
                    <Fact label="Model" value={eq.model} />
                    <Fact label="Serial no." value={eq.serialNo} />
                    <Fact label="Ownership" value={mntLabel(eq.ownership)} />
                    <Fact label="Owner / lessor" value={eq.ownerParty} />
                    <Fact label="Rental until" value={eq.rentalUntil ? formatDate(eq.rentalUntil) : undefined} />
                    <Fact label="Parent equipment" value={eq.parentEquipmentCode} />
                    <Fact label="Fixed asset" value={eq.fixedAssetCode} />
                    <Fact label="Commissioned" value={eq.commissionedOn ? formatDate(eq.commissionedOn) : undefined} />
                    <Fact label="Warranty until" value={eq.warrantyUntil ? formatDate(eq.warrantyUntil) : undefined} />
                    {eq.status === 'RETIRED' && <Fact label="Retired" value={`${eq.retiredOn ? formatDate(eq.retiredOn) : ''} ${eq.retiredReason ?? ''}`} />}
                    <Fact label="Notes" value={eq.notes} />
                </Box>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.equipmentDetail.meters">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Meters</Typography>
                    {canManage && <Button data-help="maint.equipmentDetail.addMeter" size="small" onClick={() => setMeterOpen({ req: { ...EMPTY_METER } })}>Add meter</Button>}
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="maint.equipmentDetail.mcol.code">Code</TableCell><TableCell data-help="maint.equipmentDetail.mcol.name">Name</TableCell><TableCell data-help="maint.equipmentDetail.mcol.kind">Kind</TableCell>
                                <TableCell data-help="maint.equipmentDetail.mcol.unit">Unit</TableCell><TableCell data-help="maint.equipmentDetail.mcol.driver">PM driver</TableCell><TableCell data-help="maint.equipmentDetail.mcol.last">Last reading</TableCell>
                                <TableCell data-help="maint.equipmentDetail.mcol.actions" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(eq.meters ?? []).length === 0 && <TableRow><TableCell colSpan={7} align="center">No meters</TableCell></TableRow>}
                            {(eq.meters ?? []).map(m => (
                                <TableRow key={m.id}>
                                    <TableCell>{m.code}</TableCell>
                                    <TableCell>{m.name}{!m.active && ' (inactive)'}</TableCell>
                                    <TableCell>{mntLabel(m.kind)}</TableCell>
                                    <TableCell>{m.unit ?? '-'}</TableCell>
                                    <TableCell>{m.isPmDriver ? 'Yes' : 'No'}</TableCell>
                                    <TableCell>{m.lastValue ?? '-'}{m.lastReadAt ? ` (${formatDateTime(m.lastReadAt)})` : ''}</TableCell>
                                    <TableCell align="right">
                                        {canManage && (
                                            <>
                                                <IconButton data-help="maint.equipmentDetail.action.editMeter" size="small" aria-label="Edit meter" onClick={() => setMeterOpen({
                                                    id: m.id,
                                                    req: { code: m.code, name: m.name, kind: m.kind, unit: m.unit ?? '', minValue: m.minValue, maxValue: m.maxValue, isPmDriver: m.isPmDriver, active: m.active },
                                                })}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton data-help="maint.equipmentDetail.action.deleteMeter" size="small" aria-label="Delete meter" onClick={() => removeMeter(m)}><DeleteIcon fontSize="small" /></IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            {canViewPm && (
                <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.equipmentDetail.pm">
                    <Typography variant="h6" sx={{ mb: 1 }}>PM plans</Typography>
                    {pmPlans.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No preventive maintenance plans for this machine.</Typography>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="maint.equipmentDetail.pcol.plan">Plan</TableCell><TableCell data-help="maint.equipmentDetail.pcol.repeats">Repeats</TableCell><TableCell data-help="maint.equipmentDetail.pcol.next">Next due</TableCell><TableCell data-help="maint.equipmentDetail.pcol.status">Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pmPlans.map(pl => (
                                        <TableRow key={pl.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/maintenance/pm-plans/${pl.id}`)}>
                                            <TableCell>{pl.name}</TableCell>
                                            <TableCell>{pmTriggerText(pl)}</TableCell>
                                            <TableCell>{pmNextDueText(pl)}</TableCell>
                                            <TableCell><Chip size="small" label={PM_STATE_LABEL[pl.state]} color={PM_STATE_COLOR[pl.state]} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </Paper>
            )}

            {canViewWo && (
                <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.equipmentDetail.workOrders">
                    <Typography variant="h6" sx={{ mb: 1 }}>Work order history</Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow><TableCell data-help="maint.equipmentDetail.wcol.no">No.</TableCell><TableCell data-help="maint.equipmentDetail.wcol.type">Type</TableCell><TableCell data-help="maint.equipmentDetail.wcol.title">Title</TableCell><TableCell data-help="maint.equipmentDetail.wcol.status">Status</TableCell></TableRow>
                            </TableHead>
                            <TableBody>
                                {workOrders.length === 0 && <TableRow><TableCell colSpan={4} align="center">No work orders for this machine</TableCell></TableRow>}
                                {workOrders.map(w => (
                                    <TableRow key={w.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/maintenance/work-orders/${w.id}`)}>
                                        <TableCell>{w.woNo}</TableCell>
                                        <TableCell>{mntLabel(w.type)}</TableCell>
                                        <TableCell>{w.title}</TableCell>
                                        <TableCell>{mntLabel(w.status)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>
            )}

            <Paper sx={{ p: 2 }} data-help-section="maint.equipmentDetail.history">
                <Typography variant="h6" sx={{ mb: 1 }}>Location history</Typography>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow><TableCell data-help="maint.equipmentDetail.hcol.when">When</TableCell><TableCell data-help="maint.equipmentDetail.hcol.from">From</TableCell><TableCell data-help="maint.equipmentDetail.hcol.to">To</TableCell><TableCell data-help="maint.equipmentDetail.hcol.reason">Reason</TableCell></TableRow>
                        </TableHead>
                        <TableBody>
                            {moves.length === 0 && <TableRow><TableCell colSpan={4} align="center">No moves recorded</TableCell></TableRow>}
                            {moves.map(m => (
                                <TableRow key={m.id}>
                                    <TableCell>{formatDateTime(m.movedAt)}</TableCell>
                                    <TableCell>{m.fromDepartmentName ?? '-'}</TableCell>
                                    <TableCell>{m.toDepartmentName ?? '-'}</TableCell>
                                    <TableCell>{m.reason ?? '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            <EquipmentLabelDialog open={labelOpen} equipmentIds={[eq.id]} onClose={() => setLabelOpen(false)} />
            <ReportBreakdownDialog open={reportOpen} initialEquipment={{ id: eq.id, code: eq.code, name: eq.name, locationName: eq.locationDepartmentName ?? null }} onClose={() => setReportOpen(false)}
                onCreated={() => undefined} />

            <Dialog open={moveOpen} onClose={() => setMoveOpen(false)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'maint.equipmentMove.page' } as any}>
                <DialogTitle>Move equipment</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <SearchableSelect
                        options={departments} value={toDept} size="small" label="New location" required data-help="maint.equipmentMove.field.toDept"
                        getOptionLabel={d => d.name} onChange={setToDept}
                    />
                    <TextField size="small" label="Reason" value={reason} onChange={e => setReason(e.target.value)} data-help="maint.equipmentMove.field.reason" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMoveOpen(false)} data-help="maint.equipmentMove.cancel">Cancel</Button>
                    <Button variant="contained" onClick={doMove} data-help="maint.equipmentMove.confirm">Move</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={statusOpen} onClose={() => setStatusOpen(false)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'maint.equipmentStatus.page' } as any}>
                <DialogTitle>Change status</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField select size="small" label="Status" value={newStatus} data-help="maint.equipmentStatus.field.status" onChange={e => setNewStatus(e.target.value as EquipmentStatus)}>
                        {EQUIPMENT_STATUSES.map(s => <MenuItem key={s} value={s}>{mntLabel(s)}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="Reason" value={reason} onChange={e => setReason(e.target.value)}
                        helperText={newStatus === 'RETIRED' ? 'Retiring is permanent; give the reason' : undefined} data-help="maint.equipmentStatus.field.reason" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusOpen(false)} data-help="maint.equipmentStatus.cancel">Cancel</Button>
                    <Button variant="contained" onClick={doStatus} data-help="maint.equipmentStatus.confirm">Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!meterOpen} onClose={() => setMeterOpen(null)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'maint.meterForm.page' } as any}>
                <DialogTitle>{meterOpen?.id ? 'Edit meter' : 'Add meter'}</DialogTitle>
                {meterOpen && (
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <TextField size="small" label="Code" required data-help="maint.meterForm.field.code" value={meterOpen.req.code}
                            onChange={e => setMeterOpen({ ...meterOpen, req: { ...meterOpen.req, code: e.target.value } })} />
                        <TextField size="small" label="Name" required data-help="maint.meterForm.field.name" value={meterOpen.req.name}
                            onChange={e => setMeterOpen({ ...meterOpen, req: { ...meterOpen.req, name: e.target.value } })} />
                        <TextField select size="small" label="Kind" value={meterOpen.req.kind} data-help="maint.meterForm.field.kind"
                            onChange={e => setMeterOpen({ ...meterOpen, req: { ...meterOpen.req, kind: e.target.value as MeterKind } })}>
                            {METER_KINDS.map(k => <MenuItem key={k} value={k}>{mntLabel(k)}</MenuItem>)}
                        </TextField>
                        <TextField size="small" label="Unit" value={meterOpen.req.unit ?? ''} data-help="maint.meterForm.field.unit"
                            onChange={e => setMeterOpen({ ...meterOpen, req: { ...meterOpen.req, unit: e.target.value } })} />
                        <FormControlLabel label="Drives preventive maintenance" data-help="maint.meterForm.field.pmDriver" control={
                            <Checkbox checked={!!meterOpen.req.isPmDriver}
                                onChange={e => setMeterOpen({ ...meterOpen, req: { ...meterOpen.req, isPmDriver: e.target.checked } })} />} />
                        <FormControlLabel label="Active" data-help="maint.meterForm.field.active" control={
                            <Checkbox checked={meterOpen.req.active !== false}
                                onChange={e => setMeterOpen({ ...meterOpen, req: { ...meterOpen.req, active: e.target.checked } })} />} />
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setMeterOpen(null)} data-help="maint.meterForm.cancel">Cancel</Button>
                    <Button variant="contained" onClick={saveMeter} data-help="maint.meterForm.save">Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EquipmentDetail;
